<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <dropdown
          :label="$t('order_status')"
          :items="statusItems"
          v-model="orderStatus"
          hide-details
        ></dropdown>
      </v-col>
    </v-row>

    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="customSearch(users, headers, search)"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-account-off </v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_employee_report_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>
      <template v-slot:item.num="{ item }">
        {{ item.num }}
      </template>
      <template v-slot:item.id="{ item }">
        <span @click="openUserDialog(item)">{{ item.id }} </span>
      </template>
      <template v-slot:item.shift="{ item }">
        <span v-if="dates.from === dates.to && item.shift">
          {{ formatShift(item.shift) }}
        </span>
      </template>
      <template v-slot:item.date="{ item }">
        <span
          :title="$t('order_datetime')"
          v-if="dates.from === dates.to && item.order_count > 0"
        >
          {{ item.date }}
        </span>
      </template>
      <template v-slot:item.action="{ item }">
        <circle-button
          icon="mdi-format-list-bulleted"
          @click="openDialogReportUser(item)"
          color="primary"
          :tooltip="$t('tooltip_order_details')"
        />
      </template>
      <template v-slot:item.order_count="{ item }">
        {{ item.order_count }}
      </template>
      <template v-slot:item.order_payed="{ item }">
        {{ item.order_payed }}
      </template>
      <template v-slot:item.shift_one="{ item }">
        {{ item.shift_one }}
      </template>
      <template v-slot:item.shift_two="{ item }">
        {{ item.shift_two }}
      </template>
      <template v-slot:item.shift_three="{ item }">
        {{ item.shift_three }}
      </template>
      <template v-slot:item.shift_four="{ item }">
        {{ item.shift_four }}
      </template>
      <template v-slot:body.append>
        <tr class="font-weight-bold totals-row">
          <td v-if="!device.isMobile"></td>
          <td>{{ $t("totalU") }}</td>
          <td></td>
          <td></td>
          <td v-if="dates.from === dates.to"></td>
          <td v-if="dates.from === dates.to"></td>
          <td class="text-center" v-if="selectedShifts.includes(1)">
            {{ getTotalForColumn("shift_one") }}
          </td>
          <td class="text-center" v-if="selectedShifts.includes(2)">
            {{ getTotalForColumn("shift_two") }}
          </td>
          <td class="text-center" v-if="selectedShifts.includes(3)">
            {{ getTotalForColumn("shift_three") }}
          </td>
          <td class="text-center" v-if="selectedShifts.includes(4)">
            {{ getTotalForColumn("shift_four") }}
          </td>
          <td class="text-center">{{ getTotalForColumn("order_count") }}</td>
          <td class="text-center">{{ getTotalForColumn("order_payed") }}</td>
          <td class="text-center">{{ getTotalForColumn("order_difference") }}</td>
          <td class="text-center"></td>
        </tr>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="customSearch(users, headers, search)"
      :loading="loading"
      item-key="id"
      :fields-per-row="3"
      :bold-fields="['name']"
      :show-row-number="false"
      class="px-3"
      @click:item="openDialogReportUser"
    >
      <template v-slot:item.name="{ item }">
        {{ item.name }}
      </template>
      <template v-slot:item.date="{ item }">
        {{ item.date }}
      </template>
      <template v-slot:item.shift="{ item }">
        <span v-if="dates.from === dates.to && item.shift">
          {{ formatShift(item.shift) }}
        </span>
      </template>
    </mobile-data-cards>

    <report-employee-order-history-dialog
      v-model="orderHistoryDialog"
      :employee="userReportItem"
      :company="company"
    />
    <report-employee-dialog v-model="showUserDialog" :user="selectedUser" />
  </div>
</template>

<script>
import limitService from "@/services/limit";
import moment from "moment";
import ReportEmployeeOrderHistoryDialog from "@/components/report/more-restaurants/dialogs/ReportEmployeeOrderHistoryDialogMoreRestaurants.vue";
import ReportEmployeeDialog from "@/components/report/employee/dialogs/ReportEmployeeDialog.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import { SHIFTS } from "@/constants/shifts";

export default {
  name: "CompanyMultiRestaurantTabEmployees",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    ReportEmployeeOrderHistoryDialog,
    ReportEmployeeDialog,
    MobileDataCards,
  },
  data: () => ({
    orderHistoryDialog: false,
    userReportItem: null,
    report: null,
    loading: false,
    selectedUser: null,
    showUserDialog: false,
    orderStatus: 0,
  }),
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    company() {
      return this.$store.state.company;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    selectedShifts() {
      return this.$store.state.report.filters.selectedShifts || [1, 2, 3];
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    statusItems() {
      return [
        { text: this.$t("all"), value: 0 },
        { text: this.$t("users_ordered"), value: 1 },
        { text: this.$t("not_ordered"), value: 2 },
        { text: this.$t("users_ordered_not_taken"), value: 3 },
      ];
    },
    filteredUsers() {
      const userGroups = Array.isArray(this.report) ? this.report : [];

      const allUsers = userGroups
        .map((group) => {
          const topUser = group.user || {};

          const txs = (group.limitTransactions || []).filter((t) => {
            if (t.orderable_id === null) return false;
            if (t.company_id !== this.companyId) return false;
            if (
              this.selectedRestaurants.length > 0 &&
              !this.selectedRestaurants.includes(t.restaurant_id)
            )
              return false;
            const txDate = (t.date || "").substring(0, 10);
            if (txDate < this.dates.from || txDate > this.dates.to) return false;
            return true;
          });

          const userFromTx = txs.length > 0 ? txs[0].user : null;
          const firstTx = txs[0] || null;

          const order_count = txs.length;
          const order_payed = txs.filter((t) => t.order?.state === 1).length;

          return {
            id: topUser.id,
            name: userFromTx?.name || topUser.name || "",
            lastname: userFromTx?.lastname || "",
            external_code: "",
            shift_one: txs.filter((t) => t.shift === "1-smena").length,
            shift_two: txs.filter((t) => t.shift === "2-smena").length,
            shift_three: txs.filter((t) => t.shift === "3-smena").length,
            shift_four: txs.filter((t) => t.shift === "4-smena").length,
            order_count,
            order_payed,
            date: firstTx?.order?.created_at || firstTx?.created_at || "",
            shift: firstTx?.shift || "",
          };
        })
        .filter((u) => u.id);

      if (this.orderStatus === 0) return allUsers;

      return allUsers.filter((user) => {
        const hasOrders = (user.order_count || 0) > 0;
        const hasTaken = (user.order_payed || 0) > 0;
        switch (this.orderStatus) {
          case 1:
            return hasOrders;
          case 2:
            return !hasOrders;
          case 3:
            return hasOrders && !hasTaken;
          default:
            return true;
        }
      });
    },
    users() {
      const mappedUsers = this.filteredUsers.map((user, index) => {
        return {
          ...user,
          name: `${this.toLatin(user.name)} ${this.toLatin(user.lastname)}`.trim(),
          num: index + 1,
          date:
            user.date &&
            user.date !== "0000-00-00 00:00:00" &&
            moment(user.date, "YYYY-MM-DD HH:mm:ss").isValid()
              ? moment(user.date, "YYYY-MM-DD HH:mm:ss").format("DD.MM.YYYY HH:mm")
              : "",
          order_difference: (user.order_count || 0) - (user.order_payed || 0),
        };
      });

      return mappedUsers;
    },
    headers() {
      const h = [
        { text: this.$t("r_b"), value: "num", sortable: false, print: false },
        { text: "ID", value: "id", sortable: false },
        { text: this.$t("external_code"), value: "external_code" },
        { text: this.$t("fullname"), value: "name", sortable: false },
      ];

      if (this.dates.from === this.dates.to) {
        h.push(
          {
            text: this.$t("date") + "/" + this.$t("time"),
            value: "date",
            sortable: false,
          },
          { text: this.$t("shifts"), value: "shift" }
        );
      }

      const shiftHeaders = SHIFTS.filter((s) =>
        this.selectedShifts.includes(s.id)
      ).map((s) => ({ text: this.$t(s.label), value: s.key, align: "center" }));

      return [
        ...h,
        ...shiftHeaders,
        { text: this.$t("ordered"), value: "order_count", align: "center" },
        { text: this.$t("taken"), value: "order_payed", align: "center" },
        { text: this.$t("difference"), value: "order_difference", align: "center" },
        {
          text: this.$t("report"),
          value: "action",
          sortable: false,
          print: false,
          align: "center",
          width: "110px",
        },
      ];
    },
    mobileHeaders() {
      const h = [
        { text: "ID", value: "id" },
        { text: this.$t("fullname"), value: "name" },
        { text: this.$t("external_code"), value: "external_code" },
      ];
      if (this.dates.from === this.dates.to) {
        h.push(
          { text: this.$t("date") + "/" + this.$t("time"), value: "date" },
          { text: this.$t("shifts"), value: "shift" }
        );
      }
      return [
        ...h,
        { text: this.$t("ordered"), value: "order_count" },
        { text: this.$t("taken"), value: "order_payed" },
        { text: this.$t("difference"), value: "order_difference" },
      ];
    },
  },
  watch: {
    companyId() {
      this.getUsers();
    },
    dates: {
      handler() {
        this.getUsers();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("employees")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("employees"),
            headers: this.getExportHeaders(),
            items: this.formatUsersForExport(),
            title: [this.$t("report"), this.$t("employees")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const restaurantNames = this.selectedRestaurants
        .map((id) => this.restaurants.find((r) => r.id === id)?.name)
        .filter(Boolean)
        .map(this.toLatin)
        .join(", ");
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.formatUsersForExport(),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("employees")].join(": "),
        subtitle: this.subtitle,
        afterTableHtml: `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
          <strong>${this.$t("restaurants")}:</strong> ${restaurantNames}
        </div>`,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    formatShift(shift) {
      if (Number.isInteger(shift)) return shift;
      return typeof shift === "string" ? shift.replace("-smena", "") : "";
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatUsersForExport() {
      const exportHeaders = this.getExportHeaders();
      return this.users.map((item) => {
        const exportItem = {};
        exportHeaders.forEach((header) => {
          if (header.value === "shift") {
            exportItem[header.value] = this.formatShift(item[header.value]);
          } else {
            exportItem[header.value] =
              item[header.value] ?? (typeof item[header.value] === "number" ? 0 : "");
          }
        });
        return exportItem;
      });
    },
    async getUsers() {
      if (!this.companyId) {
        this.report = null;
        return;
      }
      this.loading = true;
      this.report = null;
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId, {
          date: this.dates.from,
          date2: this.dates.to,
        });
        this.report = data;
      } catch (error) {
        this.report = null;
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },

    openDialogReportUser(item) {
      this.userReportItem = item;
      this.orderHistoryDialog = true;
    },
    openUserDialog(user) {
      this.selectedUser = user;
      this.showUserDialog = true;
    },

    getTotalForColumn(column) {
      if (!this.filteredUsers || this.filteredUsers.length === 0) return 0;
      return this.filteredUsers.reduce((total, user) => total + (user[column] || 0), 0);
    },
  },
};
</script>
